import {
  ArrowCircleGray400Icon,
  ChevronDownGray600Icon,
  ChevronRightGray600Icon,
  EyeCrossLineWhiteIcon,
  EyeGray400Icon,
  RadioSelectedIcon,
  RadioUnselectedIcon,
  ToggleOffIcon,
  ToggleOnIcon,
  XGray400Icon
} from '@assets/iconComponents';
import {
  CharacterNumberInput,
  CheckboxItem,
  Chip,
  ColumnNumAdjuster,
  ContiTextarea,
  Frame,
  MenuTop,
  Output,
  SearchInput,
  SearchResetButton,
  Select,
  TagSearchInput,
  Text
} from '@components';
import {
  AgeRangeChildOptionValueEnum,
  CharacterNumberInputType,
  ChipPaperType,
  HorizontalAngleChildOptionValueEnum,
  MainMotionPartChildOptionValueEnum,
  OutputSortTypeEnum,
  PostureCategoryChildOptionValueEnum,
  SelectedComicOptionEnum,
  StandardFacialExpressionChildOptionValueEnum,
  StandardGenreValueEnum,
  StandardShotChildOptionValueEnum,
  styleConstant,
  VerticalAngleChildOptionValueEnum
} from '@constant';
import { useComic, useMetaTag, useOutput, useStandard } from '@hooks';
import { MenuItem, Radio, styled } from '@mui/material';
import { useComicActions, useMetaTagActions, useStandardActions } from '@remote-hooks';
import { colorSet } from '@theme';
import { useEffect, useRef, useState } from 'react';

// 스크롤바 커스터마이징을 위해 MUI styled component 이용
const LeftContainer = styled('div')({
  width: '100%',
  height: '100%',
  overflowY: 'auto',
  paddingRight: '8px',

  '&::-webkit-scrollbar': {
    width: '8px',
    height: '0px'
  },

  '&::-webkit-scrollbar-thumb': {
    borderRadius: '8px',
    backgroundColor: colorSet.primaryMain
  },

  '&::-webkit-scrollbar-track': {
    borderRadius: '8px',
    backgroundColor: colorSet.gray200
  }
});

const StandardOutputPage = () => {
  const {
    handleSidebarRefreshButtonClick,
    sectionExpandStates,
    handleSectionExpand,
    contiText,
    handleContiTextChange,
    isFetchTextAIRecommendationLoading,
    handleContiSearchButtonClick,
    handleComicSearchToggle,
    isComicSectionExpanded,
    isComicToggleOn
  } = useStandard();

  const {
    selectedMetaTags,
    tagInputKeyword,
    handleTagKeyDown,
    handleTagInputKeywordChange,
    tagAutocompleteList,
    handleTagAutocompleteChange,
    handleSingleTagClear,
    handleTagClear,
    handleTagSubmit,

    aiRecommendedMetaTags,
    handleSingleAiRecommendedTagClear
  } = useMetaTag();

  const {
    initComic,
    selectedOption,
    handleOptionChange,
    selectedGenreList,
    handleGenreToggle,
    isGenreSelected,
    selectedComicNameList,
    inputKeyword,
    autocompleteList,
    handleKeyDown,
    handleInputKeywordChange,
    handleAutocompleteChange,
    handleComicTagClear,
    initComicNameInput
  } = useComic();

  const scrollToTop = () => {
    if (rightContainerRef.current) {
      rightContainerRef.current.scrollTop = 0;
    }
  };

  const {
    minColumnNum,
    maxColumnNum,
    columnNum,
    setColumnNum,
    storedStandard,
    onSetStandard,
    handleIncludeUsedToggle,
    handleIncludeOtherUsersUsedToggle
  } = useOutput(scrollToTop);

  // 마운트 상태를 추적하는 state
  const [isComponentMounted, setIsComponentMounted] = useState(false);
  // auto hide navbar 관련 state
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const rightContainerRef = useRef<HTMLDivElement>(null);

  // StandardGenreValueEnum의 값들을 배열로 변환
  const genreOptions = Object.values(StandardGenreValueEnum);

  // 정렬 기준 Select 컴포넌트의 value를 영어가 아닌 한국어로 보여주기 위한 변수
  const SORT_LABELS: Record<OutputSortTypeEnum, string> = {
    [OutputSortTypeEnum.RANDOM]: '랜덤순',
    [OutputSortTypeEnum.LOW_USAGE]: '사용량 낮음'
  };

  // 인원수 의존성 필터 disabled 로직을 위한 변수
  const isCharacterNumberOne = storedStandard.characterNumber
    ? storedStandard.characterNumber.isWholeGenderNumberChecked
      ? storedStandard.characterNumber.wholeGenderNumber === 1
      : typeof storedStandard.characterNumber.maleNumber === 'number' &&
        typeof storedStandard.characterNumber.femaleNumber === 'number' &&
        storedStandard.characterNumber.maleNumber + storedStandard.characterNumber.femaleNumber === 1
    : false;

  const { onInitComic } = useComicActions();
  const { onInitMetaTag } = useMetaTagActions();
  const { onInitStandard } = useStandardActions();

  // init store
  useEffect(() => {
    setIsComponentMounted(true);
    if (!isComponentMounted) return;

    return () => {
      onInitComic();
      onInitMetaTag();
      onInitStandard();
    };
  }, [isComponentMounted, onInitComic, onInitMetaTag, onInitStandard]);

  // auto hide navbar 스크롤 이벤트 핸들러
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = rightContainerRef.current?.scrollTop || 0;

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // 아래로 스크롤하고 100px 이상 스크롤했을 때 navbar 숨기기
        setIsNavbarVisible(false);
      } else if (currentScrollY < lastScrollY) {
        // 위로 스크롤할 때 navbar 보이기
        setIsNavbarVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    const rightContainer = rightContainerRef.current;
    if (rightContainer) {
      rightContainer.addEventListener('scroll', handleScroll);
      return () => rightContainer.removeEventListener('scroll', handleScroll);
    }
  }, [lastScrollY]);

  return (
    <>
      <MenuTop />

      <div
        style={{
          height: `calc(100vh - ${styleConstant.menuTopHeight}px)`,
          display: 'flex',
          flexDirection: 'row'
        }}
      >
        {/* 좌측 메뉴 */}
        <div
          style={{
            width: '352px',
            height: '100%',
            padding: '4px 8px 4px 16px'
          }}
        >
          <LeftContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '20px 0px' }}>
              {/* 좌측 메뉴 항목들 */}
              {/* 제목 및 초기화 버튼 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text variant="h2" fontWeight="bold">
                  스탠다드 검색
                </Text>
                <ArrowCircleGray400Icon
                  onClick={() => {
                    handleTagClear(); // '관련 태그' 인풋 텍스트 제거
                    initComicNameInput(); // '작품명' 인풋 텍스트 제거
                    handleSidebarRefreshButtonClick();
                  }}
                  style={{ cursor: 'pointer' }}
                />
              </div>
              {/* 글콘티 */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <div
                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    onClick={() => handleSectionExpand('conti')}
                  >
                    {sectionExpandStates.conti ? <ChevronDownGray600Icon /> : <ChevronRightGray600Icon />}
                  </div>
                  <Text variant="subtitle" fontWeight="bold">
                    글콘티
                  </Text>
                </div>
                {sectionExpandStates.conti ? (
                  <div>
                    <ContiTextarea
                      value={contiText}
                      onChange={handleContiTextChange}
                      isbuttondisabled={contiText.trim().length === 0 || isFetchTextAIRecommendationLoading}
                      onButtonClick={handleContiSearchButtonClick}
                    />
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: `4px`,
                        width: '100%',
                        boxSizing: 'border-box',
                        marginTop: aiRecommendedMetaTags && aiRecommendedMetaTags.length > 0 ? 10 : 0
                      }}
                    >
                      {aiRecommendedMetaTags && aiRecommendedMetaTags.length > 0
                        ? aiRecommendedMetaTags.map((item, index) => {
                            return (
                              <div
                                key={index}
                                style={{
                                  height: 28,
                                  border: `1px solid ${colorSet.gray300}`,
                                  borderRadius: '6px',
                                  padding: '0px 10px',
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                <Text variant="body1" marginRight="2px">
                                  {item}
                                </Text>
                                <XGray400Icon
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    handleSingleAiRecommendedTagClear(item);
                                  }}
                                />
                              </div>
                            );
                          })
                        : null}
                    </div>
                  </div>
                ) : null}
              </div>
              {/* 관련 태그 */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <div
                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    onClick={() => handleSectionExpand('tag')}
                  >
                    {sectionExpandStates.tag ? <ChevronDownGray600Icon /> : <ChevronRightGray600Icon />}
                  </div>
                  <Text variant="subtitle" fontWeight="bold">
                    관련 태그
                  </Text>
                </div>
                {sectionExpandStates.tag ? (
                  <div>
                    <SearchInput
                      placeholder="태그 입력 후 스페이스바를 누르세요."
                      value={tagInputKeyword}
                      options={tagAutocompleteList}
                      onKeyDown={handleTagKeyDown}
                      onInputChange={handleTagInputKeywordChange}
                      onAutocompleteChange={handleTagAutocompleteChange}
                      onClear={handleTagClear}
                      onSubmit={handleTagSubmit}
                      showTwoSubIcons={true}
                    />
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: `4px`,
                        width: '100%',
                        boxSizing: 'border-box',
                        marginTop: selectedMetaTags && selectedMetaTags.length > 0 ? 10 : 0
                      }}
                    >
                      {selectedMetaTags && selectedMetaTags.length > 0
                        ? selectedMetaTags.map((item, index) => {
                            return (
                              <div
                                key={index}
                                style={{
                                  height: 28,
                                  border: `1px solid ${colorSet.gray300}`,
                                  borderRadius: '6px',
                                  padding: '0px 10px',
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                <Text variant="body1" marginRight="2px">
                                  {item}
                                </Text>
                                <XGray400Icon
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    handleSingleTagClear(item);
                                  }}
                                />
                              </div>
                            );
                          })
                        : null}
                    </div>
                  </div>
                ) : null}
              </div>
              {/* 장르 및 작품명 */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <div
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                      onClick={() => handleSectionExpand('comic')}
                    >
                      {isComicSectionExpanded ? <ChevronDownGray600Icon /> : <ChevronRightGray600Icon />}
                    </div>
                    <Text variant="subtitle" fontWeight="bold">
                      작품 정보로 검색
                    </Text>
                  </div>
                  <div
                    onClick={() => {
                      initComic();
                      handleComicSearchToggle();
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {isComicToggleOn ? <ToggleOnIcon /> : <ToggleOffIcon />}
                  </div>
                </div>
                {isComicSectionExpanded ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', position: 'relative' }}>
                    {/* 장르 */}
                    <div>
                      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: '8px' }}>
                        <Radio
                          disabled={!isComicToggleOn}
                          checked={selectedOption === SelectedComicOptionEnum.GENRE}
                          onChange={() => {
                            handleOptionChange(SelectedComicOptionEnum.GENRE);
                          }}
                          checkedIcon={<RadioSelectedIcon style={{ width: 20, height: 20 }} />}
                          icon={<RadioUnselectedIcon style={{ width: 20, height: 20 }} />}
                        />
                        <Text variant="subtitle">장르</Text>
                      </div>
                      <Select
                        disabled={selectedOption !== SelectedComicOptionEnum.GENRE}
                        multiple={true}
                        value={selectedGenreList}
                        renderValue={(selected: any) => {
                          if (selected && selected.length > 0) {
                            return selected.join(', ');
                          } else {
                            return (
                              <Text variant="body1" color={colorSet.gray400}>
                                {selectedOption !== SelectedComicOptionEnum.GENRE
                                  ? '작품명 입력 시 장르는 자동 선택됩니다.'
                                  : '장르를 1개 이상 선택해주세요.'}
                              </Text>
                            );
                          }
                        }}
                        boxstyle={{ width: '100%' }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 386,
                              '&::-webkit-scrollbar': {
                                width: '8px',
                                height: '0px'
                              },
                              '&::-webkit-scrollbar-thumb': {
                                borderRadius: '8px',
                                backgroundColor: colorSet.primaryMain
                              },
                              '&::-webkit-scrollbar-track': {
                                borderRadius: '8px',
                                backgroundColor: colorSet.gray200
                              },
                              '& .MuiMenuItem-root.Mui-selected': {
                                backgroundColor: colorSet.white
                              }
                            }
                          }
                        }}
                      >
                        <MenuItem value={''} disabled>
                          장르
                        </MenuItem>
                        {genreOptions.map((genre) => (
                          <MenuItem key={genre} value={genre} onClick={() => handleGenreToggle(genre)}>
                            <CheckboxItem
                              label={genre}
                              isChecked={isGenreSelected(genre)}
                              onChange={() => {
                                //onClick은 MenuItem에만 적용
                              }}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                    {/* 작품명 */}
                    <div>
                      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: '8px' }}>
                        <Radio
                          disabled={!isComicToggleOn}
                          checked={selectedOption === SelectedComicOptionEnum.COMIC_NAME}
                          onChange={() => {
                            handleOptionChange(SelectedComicOptionEnum.COMIC_NAME);
                          }}
                          checkedIcon={<RadioSelectedIcon style={{ width: 20, height: 20 }} />}
                          icon={<RadioUnselectedIcon style={{ width: 20, height: 20 }} />}
                        />
                        <Text variant="subtitle">작품명</Text>
                      </div>
                      <TagSearchInput
                        placeholder={
                          selectedOption !== SelectedComicOptionEnum.COMIC_NAME
                            ? '장르 선택 시 작품은 전체 검색됩니다.'
                            : '작품명 입력 후 엔터를 누르세요.'
                        }
                        tags={selectedComicNameList}
                        inputValue={inputKeyword}
                        options={autocompleteList}
                        onKeyDown={handleKeyDown}
                        onInputChange={handleInputKeywordChange}
                        onAutocompleteChange={handleAutocompleteChange}
                        onTagClear={handleComicTagClear}
                        disabled={selectedOption !== SelectedComicOptionEnum.COMIC_NAME}
                        boxstyle={{ width: '100%' }}
                      />
                    </div>
                    {!isComicToggleOn ? (
                      <div
                        style={{
                          width: '100%', // 부모 div의 너비를 채우도록 설정
                          height: '100%', // 부모 div의 높이를 채우도록 설정
                          borderRadius: '6px',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          backgroundColor: 'rgba(255, 255, 255, 0.4)'
                        }}
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </LeftContainer>
        </div>

        <div style={{ height: '100%', borderRight: `1px solid ${colorSet.gray200}` }} />

        {/* 우측 이미지 리스트 */}
        <div
          ref={rightContainerRef}
          className="hide-scrollbar"
          style={{
            flex: 1,
            height: '100%',
            backgroundColor: colorSet.white,
            overflowY: 'auto'
          }}
        >
          {/* 상단: 필터 초기화 & Chip */}
          <div
            id="auto-hide-navbar"
            style={{
              width: '100%',
              position: 'sticky',
              top: 0,
              backgroundColor: colorSet.white,
              padding: '24px 40px',
              zIndex: styleConstant.zIndexTopFilter,
              transform: isNavbarVisible ? 'translateY(0)' : 'translateY(-100%)',
              transition: 'transform 0.3s ease-in-out'
            }}
          >
            <SearchResetButton boxstyle={{ marginBottom: '12px' }} />
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: `12px`,
                width: '100%',
                boxSizing: 'border-box'
              }}
            >
              <Chip
                label="배경컷"
                needTooltip={true}
                tooltipText={`배경컷이 포함된 경우, 다음 항목은 선택할 수 없습니다.
                          
                      인원수 / 연령대 / 스킨십 / 중심 동작 부위 / 표정 / 가로앵글 / 데포르메 / 동작구분`}
                options={{
                  포함: { name: '포함', value: 'true', isSelected: false },
                  제외: { name: '제외', value: 'false', isSelected: false }
                }}
                storedChildOptionValue={storedStandard.backgroundCut}
                setSelectedOption={(param) => {
                  const isTrueSelected = param?.포함.isSelected; // '포함'이 선택된 경우 의존성 필터값 null 부여

                  if (isTrueSelected) {
                    onSetStandard({
                      backgroundCut: param,
                      characterNumber: null,
                      ageRange: null,
                      mainMotionPart: null,
                      facialExpression: null,
                      deforme: null,
                      horizontalAngle: null,
                      postureCategory: null,
                      skinship: null
                    });
                  } else {
                    onSetStandard({
                      backgroundCut: param
                    });
                  }
                }}
              />
              <CharacterNumberInput
                disabled={storedStandard.backgroundCut?.포함.isSelected}
                needTooltip={true}
                tooltipText="인원수가 1명일 경우, 스킨십 항목은 선택할 수 없습니다."
                type={CharacterNumberInputType.CHIP}
                characterNumber={storedStandard.characterNumber}
                onApplyClick={(values: {
                  maleNumber?: number | string;
                  femaleNumber?: number | string;
                  wholeGenderNumber?: number | string;
                  isWholeGenderNumberChecked?: boolean;
                }) => {
                  const isCharacterNumberOne = values
                    ? values.isWholeGenderNumberChecked
                      ? values.wholeGenderNumber === 1
                      : typeof values.maleNumber === 'number' &&
                        typeof values.femaleNumber === 'number' &&
                        values.maleNumber + values.femaleNumber === 1
                    : false; // 인원수 합이 1인 경우 의존성 필터값 null 부여

                  if (isCharacterNumberOne) {
                    onSetStandard({ characterNumber: values, skinship: null });
                  } else {
                    onSetStandard({ characterNumber: values });
                  }
                }}
                onChipXButtonClick={() => {
                  onSetStandard({ characterNumber: null });
                }}
              />
              <Chip
                label="프레임"
                paperType={ChipPaperType.CHECKBOX_LIST}
                options={{
                  클로즈업: {
                    name: '클로즈업',
                    value: StandardShotChildOptionValueEnum['클로즈업'],
                    isSelected: false
                  },
                  미디엄샷: {
                    name: '미디엄샷',
                    value: StandardShotChildOptionValueEnum['미디엄샷'],
                    isSelected: false
                  },
                  풀샷: {
                    name: '풀샷',
                    value: StandardShotChildOptionValueEnum['풀샷'],
                    isSelected: false
                  },
                  롱샷: {
                    name: '롱샷',
                    value: StandardShotChildOptionValueEnum['롱샷'],
                    isSelected: false
                  }
                }}
                storedChildOptionValue={storedStandard.shot}
                setSelectedOption={(param) => {
                  onSetStandard({
                    shot: param
                  });
                }}
              />
              <Chip
                label="연출"
                needTooltip={true}
                tooltipText="주로 1인 전신 컷, 배경에 실제 배경이 아닌 분위기를 표현하는 화려한 연출이 포함됩니다."
                options={{
                  포함: { name: '포함', value: 'true', isSelected: false },
                  제외: { name: '제외', value: 'false', isSelected: false }
                }}
                storedChildOptionValue={storedStandard.directed}
                setSelectedOption={(param) => {
                  onSetStandard({
                    directed: param
                  });
                }}
              />
              <Chip
                label="세로앵글"
                paperType={ChipPaperType.CHECKBOX_LIST}
                options={{
                  로우앵글: {
                    name: '로우앵글',
                    value: VerticalAngleChildOptionValueEnum['로우앵글'],
                    isSelected: false
                  },
                  하이앵글: {
                    name: '하이앵글',
                    value: VerticalAngleChildOptionValueEnum['하이앵글'],
                    isSelected: false
                  },
                  항공샷: {
                    name: '항공샷',
                    value: VerticalAngleChildOptionValueEnum['항공샷'],
                    isSelected: false
                  },
                  평시각: {
                    name: '평시각',
                    value: VerticalAngleChildOptionValueEnum['평시각'],
                    isSelected: false
                  }
                }}
                storedChildOptionValue={storedStandard.verticalAngle}
                setSelectedOption={(param) => {
                  onSetStandard({
                    verticalAngle: param
                  });
                }}
              />
              <Chip
                label="연령대"
                disabled={storedStandard.backgroundCut?.포함.isSelected}
                paperType={ChipPaperType.CHECKBOX_LIST}
                options={{
                  유아: {
                    name: '유아',
                    value: AgeRangeChildOptionValueEnum['유아'],
                    isSelected: false
                  },
                  청소년: {
                    name: '청소년',
                    value: AgeRangeChildOptionValueEnum['청소년'],
                    isSelected: false
                  },
                  청년: {
                    name: '청년',
                    value: AgeRangeChildOptionValueEnum['청년'],
                    isSelected: false
                  },
                  중장년: {
                    name: '중장년',
                    value: AgeRangeChildOptionValueEnum['중장년'],
                    isSelected: false
                  },
                  노인: {
                    name: '노인',
                    value: AgeRangeChildOptionValueEnum['노인'],
                    isSelected: false
                  }
                }}
                storedChildOptionValue={storedStandard.ageRange}
                setSelectedOption={(param) => {
                  onSetStandard({
                    ageRange: param
                  });
                }}
              />
              <Chip
                label="중심 동작 부위"
                disabled={storedStandard.backgroundCut?.포함.isSelected}
                paperType={ChipPaperType.CHECKBOX_LIST}
                options={{
                  손: {
                    name: '손',
                    value: MainMotionPartChildOptionValueEnum['손'],
                    isSelected: false
                  },
                  팔: {
                    name: '팔',
                    value: MainMotionPartChildOptionValueEnum['팔'],
                    isSelected: false
                  },
                  다리: {
                    name: '다리',
                    value: MainMotionPartChildOptionValueEnum['다리'],
                    isSelected: false
                  },
                  발: {
                    name: '발',
                    value: MainMotionPartChildOptionValueEnum['발'],
                    isSelected: false
                  },
                  머리: {
                    name: '머리',
                    value: MainMotionPartChildOptionValueEnum['머리'],
                    isSelected: false
                  },
                  전신: {
                    name: '전신',
                    value: MainMotionPartChildOptionValueEnum['전신'],
                    isSelected: false
                  },
                  상체: {
                    name: '상체',
                    value: MainMotionPartChildOptionValueEnum['상체'],
                    isSelected: false
                  },
                  하체: {
                    name: '하체',
                    value: MainMotionPartChildOptionValueEnum['하체'],
                    isSelected: false
                  }
                }}
                storedChildOptionValue={storedStandard.mainMotionPart}
                setSelectedOption={(param) => {
                  onSetStandard({
                    mainMotionPart: param
                  });
                }}
              />
              <Chip
                label="표정"
                disabled={storedStandard.backgroundCut?.포함.isSelected}
                needTooltip={true}
                tooltipText="표정 필터를 활성화하면, 정면 또는 측면 얼굴이 명확히 드러난 이미지만 출력됩니다."
                paperType={ChipPaperType.FACE_CHECKBOX_LIST}
                options={{
                  만족: {
                    name: '만족',
                    value: StandardFacialExpressionChildOptionValueEnum['만족'],
                    isSelected: false
                  },
                  미소: {
                    name: '미소',
                    value: StandardFacialExpressionChildOptionValueEnum['미소'],
                    isSelected: false
                  },
                  새침: {
                    name: '새침',
                    value: StandardFacialExpressionChildOptionValueEnum['새침'],
                    isSelected: false
                  },
                  기쁨: {
                    name: '기쁨',
                    value: StandardFacialExpressionChildOptionValueEnum['기쁨'],
                    isSelected: false
                  },
                  폭소: {
                    name: '폭소',
                    value: StandardFacialExpressionChildOptionValueEnum['폭소'],
                    isSelected: false
                  },
                  수줍음: {
                    name: '수줍음',
                    value: StandardFacialExpressionChildOptionValueEnum['수줍음'],
                    isSelected: false
                  },
                  무표정: {
                    name: '무표정',
                    value: StandardFacialExpressionChildOptionValueEnum['무표정'],
                    isSelected: false
                  },
                  놀람: {
                    name: '놀람',
                    value: StandardFacialExpressionChildOptionValueEnum['놀람'],
                    isSelected: false
                  },
                  고민: {
                    name: '고민',
                    value: StandardFacialExpressionChildOptionValueEnum['고민'],
                    isSelected: false
                  },
                  진지: {
                    name: '진지',
                    value: StandardFacialExpressionChildOptionValueEnum['진지'],
                    isSelected: false
                  },
                  혐오: {
                    name: '혐오',
                    value: StandardFacialExpressionChildOptionValueEnum['혐오'],
                    isSelected: false
                  },
                  분노: {
                    name: '분노',
                    value: StandardFacialExpressionChildOptionValueEnum['분노'],
                    isSelected: false
                  },
                  당황: {
                    name: '당황',
                    value: StandardFacialExpressionChildOptionValueEnum['당황'],
                    isSelected: false
                  },
                  공포: {
                    name: '공포',
                    value: StandardFacialExpressionChildOptionValueEnum['공포'],
                    isSelected: false
                  },
                  슬픔: {
                    name: '슬픔',
                    value: StandardFacialExpressionChildOptionValueEnum['슬픔'],
                    isSelected: false
                  },
                  경계: {
                    name: '경계',
                    value: StandardFacialExpressionChildOptionValueEnum['경계'],
                    isSelected: false
                  },
                  좌절: {
                    name: '좌절',
                    value: StandardFacialExpressionChildOptionValueEnum['좌절'],
                    isSelected: false
                  },
                  뻔뻔: {
                    name: '뻔뻔',
                    value: StandardFacialExpressionChildOptionValueEnum['뻔뻔'],
                    isSelected: false
                  },
                  수치: {
                    name: '수치',
                    value: StandardFacialExpressionChildOptionValueEnum['수치'],
                    isSelected: false
                  },
                  의심: {
                    name: '의심',
                    value: StandardFacialExpressionChildOptionValueEnum['의심'],
                    isSelected: false
                  },
                  불안: {
                    name: '불안',
                    value: StandardFacialExpressionChildOptionValueEnum['불안'],
                    isSelected: false
                  },
                  절규: {
                    name: '절규',
                    value: StandardFacialExpressionChildOptionValueEnum['절규'],
                    isSelected: false
                  }
                }}
                storedChildOptionValue={storedStandard.facialExpression}
                setSelectedOption={(param) => {
                  onSetStandard({
                    facialExpression: param
                  });
                }}
              />
              <Chip
                label="데포르메"
                disabled={storedStandard.backgroundCut?.포함.isSelected}
                options={{
                  포함: { name: '포함', value: 'true', isSelected: false },
                  제외: { name: '제외', value: 'false', isSelected: false }
                }}
                storedChildOptionValue={storedStandard.deforme}
                setSelectedOption={(param) => {
                  onSetStandard({
                    deforme: param
                  });
                }}
              />
              <Chip
                label="가로앵글"
                disabled={storedStandard.backgroundCut?.포함.isSelected}
                paperType={ChipPaperType.CHECKBOX_LIST}
                options={{
                  정면: { name: '정면', value: HorizontalAngleChildOptionValueEnum['정면'], isSelected: false },
                  정측면: {
                    name: '정측면',
                    value: HorizontalAngleChildOptionValueEnum['정측면'],
                    isSelected: false
                  },
                  측면: { name: '측면', value: HorizontalAngleChildOptionValueEnum['측면'], isSelected: false },
                  후측면: {
                    name: '후측면',
                    value: HorizontalAngleChildOptionValueEnum['후측면'],
                    isSelected: false
                  },
                  후면: { name: '후면', value: HorizontalAngleChildOptionValueEnum['후면'], isSelected: false }
                }}
                storedChildOptionValue={storedStandard.horizontalAngle}
                setSelectedOption={(param) => {
                  onSetStandard({ horizontalAngle: param });
                }}
              />
              <Chip
                label="동작구분"
                disabled={storedStandard.backgroundCut?.포함.isSelected}
                paperType={ChipPaperType.CHECKBOX_LIST_TOGGLE}
                options={{
                  서있음: { name: '서있음', value: PostureCategoryChildOptionValueEnum['서있음'], isSelected: false },
                  앉음: {
                    name: '앉음',
                    value: PostureCategoryChildOptionValueEnum['앉음'],
                    isSelected: false
                  },
                  누움: { name: '누움', value: PostureCategoryChildOptionValueEnum['누움'], isSelected: false },
                  엎드림: {
                    name: '엎드림',
                    value: PostureCategoryChildOptionValueEnum['엎드림'],
                    isSelected: false
                  }
                }}
                storedChildOptionValue={storedStandard.postureCategory}
                setSelectedOption={(param) => {
                  onSetStandard({ postureCategory: param });
                }}
              />
              <Chip
                label="스킨십(12~17+)"
                disabled={storedStandard.backgroundCut?.포함.isSelected || isCharacterNumberOne}
                needTooltip={true}
                tooltipText={`19+를 제외한 포옹, 키스 등의 접촉 장면을 포함합니다.
                        인원수가 2명 이상일 때만 활성화됩니다.`}
                options={{
                  포함: { name: '포함', value: 'true', isSelected: false },
                  제외: { name: '제외', value: 'false', isSelected: false }
                }}
                storedChildOptionValue={storedStandard.skinship}
                setSelectedOption={(param) => {
                  onSetStandard({
                    skinship: param
                  });
                }}
              />
            </div>
          </div>
          {/* 중간 필터 & 검색 결과*/}
          <Frame width="100%" padding="0px 40px 96px 40px">
            {/* 중간 필터 */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                marginBottom: '20px'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 12
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Text variant="body1">한 줄당 이미지</Text>
                  <ColumnNumAdjuster
                    minColumnNum={minColumnNum}
                    maxColumnNum={maxColumnNum}
                    columnNum={columnNum}
                    setColumnNum={setColumnNum}
                  />
                </div>
                <div
                  onClick={handleIncludeUsedToggle}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 4,
                    padding: '8px 24px',
                    alignItems: 'center',
                    borderRadius: '22px',
                    border: storedStandard.isIncludeUsed ? `1px solid ${colorSet.gray400}` : 'none',
                    cursor: 'pointer',
                    backgroundColor: storedStandard.isIncludeUsed ? 'transparent' : colorSet.gray600
                  }}
                >
                  {storedStandard.isIncludeUsed ? <EyeGray400Icon /> : <EyeCrossLineWhiteIcon />}
                  <Text
                    variant="body2"
                    color={storedStandard.isIncludeUsed ? colorSet.gray400 : colorSet.white}
                    fontWeight={storedStandard.isIncludeUsed ? 400 : 700}
                    style={{ userSelect: 'none' }}
                  >
                    내가 사용한 이미지
                  </Text>
                </div>
                <div
                  onClick={handleIncludeOtherUsersUsedToggle}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 4,
                    padding: '8px 24px',
                    alignItems: 'center',
                    borderRadius: '22px',
                    border: storedStandard.isIncludeOtherUsersUsed ? `1px solid ${colorSet.gray400}` : 'none',
                    cursor: 'pointer',
                    backgroundColor: storedStandard.isIncludeOtherUsersUsed ? 'transparent' : colorSet.gray600
                  }}
                >
                  {storedStandard.isIncludeOtherUsersUsed ? <EyeGray400Icon /> : <EyeCrossLineWhiteIcon />}
                  <Text
                    variant="body2"
                    color={storedStandard.isIncludeOtherUsersUsed ? colorSet.gray400 : colorSet.white}
                    fontWeight={storedStandard.isIncludeOtherUsersUsed ? 400 : 700}
                    style={{ userSelect: 'none' }}
                  >
                    남이 사용한 이미지
                  </Text>
                </div>
              </div>
              <Select
                value={storedStandard.sortType}
                renderValue={(value) => {
                  const sortType = value as OutputSortTypeEnum;
                  return SORT_LABELS[sortType] || '정렬 기준';
                }}
                onChange={(e) => {
                  onSetStandard({ sortType: e.target.value });
                }}
                boxstyle={{ width: 160 }}
              >
                <MenuItem value={''} disabled>
                  정렬 기준
                </MenuItem>
                <MenuItem value={OutputSortTypeEnum.RANDOM}>랜덤순</MenuItem>
                <MenuItem value={OutputSortTypeEnum.LOW_USAGE}>사용량 낮음</MenuItem>
              </Select>
            </div>
            <Output padding="0" columnNum={columnNum} />
          </Frame>
        </div>
      </div>
    </>
  );
};

export default StandardOutputPage;
